import React from 'react';
import styles from './GstConnectionPopup.module.scss';
import clsx from 'clsx';
import { PRIMARY_GST_STATUSES } from '../../../../../store/investor/constants';
import { toTitleCaseWithSeparators } from '../../../../../utils/utils';
import { DATE_FORMATS, formatDateInIST } from '../../../../../utils/dateUtils';
import { GST_LAST_CONSENT_LABEL } from '../../../dashboardConstants';

interface GSTEntry {
    state?: string;
    gstin: string;
    connectionStatus: string;
    gstStatus?: string;
    /** Epoch ms of this GSTIN's latest consent. */
    lastConsentAt?: number;
}

interface GSTPopupProps {
    gstList: GSTEntry[];
}

function GstRow({ gst }: { gst: GSTEntry }) {
    const isPrimary = PRIMARY_GST_STATUSES.includes((gst?.gstStatus ?? '').toUpperCase());
    return (
        <div className={styles.GSTRow}>
            <div className={styles.Left}>
                <div className={styles.Identity}>
                    {gst?.state && <span className={styles.State}>{gst.state}</span>}
                    <span className={styles.GstNum}>{gst?.gstin}</span>
                </div>
                {!!gst?.lastConsentAt && (
                    <div className={styles.LastConsent}>
                        {GST_LAST_CONSENT_LABEL}
                        {formatDateInIST(gst.lastConsentAt, DATE_FORMATS.HISTORY_DRAWER)}
                    </div>
                )}
            </div>
            <div className={styles.Right}>
                <div
                    className={clsx(styles.Badge, {
                        [styles.Connected]: isPrimary && gst.connectionStatus === 'Connected',
                        [styles.NotConnected]: isPrimary && gst.connectionStatus === 'Not Connected',
                        [styles.Disconnected]: isPrimary && gst.connectionStatus === 'Disconnected',
                        [styles.Inactive]: !isPrimary,
                    })}
                >
                    {isPrimary ? gst.connectionStatus : toTitleCaseWithSeparators(gst.gstStatus ?? '')}
                </div>
            </div>
        </div>
    );
}

function GstConnectionPopup({ gstList = [] }: GSTPopupProps) {
    const primaryGsts = gstList.filter((gst) => PRIMARY_GST_STATUSES.includes((gst?.gstStatus ?? '').toUpperCase()));
    const otherGsts = gstList.filter((gst) => !PRIMARY_GST_STATUSES.includes((gst?.gstStatus ?? '').toUpperCase()));

    return (
        <div className={styles.GstPopup}>
            <div className={styles.Title}>Charts are only generated for connected GSTs:</div>
            {primaryGsts.map((gst, i) => (
                <GstRow key={i} gst={gst} />
            ))}
            {!!otherGsts.length && (
                <>
                    <div className={styles.Divider}>
                        <span className={styles.DividerLabel}>Others</span>
                        <hr className={styles.DividerLine} />
                    </div>
                    {otherGsts.map((gst, i) => (
                        <GstRow key={i} gst={gst} />
                    ))}
                </>
            )}
        </div>
    );
}

export default GstConnectionPopup;
